import { computed } from 'vue';
import { useCoursesStore } from '@/stores/courses';
import { useUserStore } from '@/stores/user';
import { REFUND_DEADLINE_DAYS } from '@/composables/useSessionMilestones';
import { parseLocalDate, toDateStr, todayDateStr } from '@/utils/dateUtils';

function calcNewDate(startDate, daysAdded) {
  if (startDate && daysAdded) {
    const date = parseLocalDate(startDate);
    // Subtract 1 because the start date counts as day 1 (inclusive)
    // e.g. 10-day drop deadline from 3/17 → last actionable day is 3/26, not 3/27
    date.setDate(date.getDate() + daysAdded - 1);
    const currDate = todayDateStr();
    const newDate = toDateStr(date);
    return newDate > currDate ? newDate : '';
  }
  return '';
}

/**
 * Calculate transcript deadline matching the card's "TRANSCRIPT BY" logic
 * (MyCourses.vue): new Date(dateString) parses YYYY-MM-DD as UTC midnight,
 * then setFullYear/getDate use local timezone — shifting the date back one
 * day in US timezones. We replicate that here so the calendar matches the card.
 */
function calcTranscriptDeadline(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  date.setFullYear(date.getFullYear() + 1);
  const newDate = toDateStr(date);
  return newDate > todayDateStr() ? newDate : '';
}

function formatDateShort(dateString) {
  if (!dateString) return '';
  const date = parseLocalDate(dateString);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function isOnDemand(term) {
  return term === 'On-demand' || term === 'Self Paced';
}

/**
 * Build calendar events from enrolled course data.
 * Only generates events for on-demand courses and per-course items (convert credit, session end).
 * Session-based enrollment/refund deadlines are driven from term data in useSessionMilestones.
 */
function buildCourseDates(enrolledCourses, isAccelerate) {
  const dates = [];

  enrolledCourses.forEach((course) => {
    const courseInfo = course._courseInfo;
    const { _courseId, _coursePrefix, _courseStartDate, _courseEndDate, _term, _courseCreditEligible } = courseInfo;

    // Convert for Credit by Date — "Last day to add X to your college transcript"
    // Must match API transcript deadline: courseCompletedDate + 1 year
    if (_courseCreditEligible && course._grade >= 0.7) {
      const courseCompletedDate = course._courseCompletedDate;
      let convertForCreditByDate;
      if (courseCompletedDate) {
        convertForCreditByDate = calcTranscriptDeadline(courseCompletedDate);
      } else if (!isOnDemand(_term) && _courseEndDate) {
        convertForCreditByDate = calcTranscriptDeadline(_courseEndDate);
      }
      if (convertForCreditByDate) {
        dates.push({
          id: _courseId,
          date: convertForCreditByDate,
          text: `Last day to add ${_coursePrefix} to your college transcript`,
          type: 'convert_credit'
        });
      }
    }

    // On-demand refund deadline (10 days from course start, exclude Benefit Type 5 and Accelerate)
    if (isOnDemand(_term)) {
      const isBenefitType5 = course._benefitType === 5 || course._benefitType === '5';
      if (!isBenefitType5 && !isAccelerate) {
        const dropDeadlineDate = calcNewDate(_courseStartDate, REFUND_DEADLINE_DAYS);
        if (dropDeadlineDate) {
          dates.push({
            id: _courseId,
            date: dropDeadlineDate,
            text: isAccelerate
              ? `Last day to avoid charges for ${_coursePrefix}`
              : `Enrollment fee refund deadline ${_coursePrefix}`,
            type: 'drop_deadline'
          });
        }
      }
    }

    // On-demand course close
    if (isOnDemand(_term) && _courseEndDate) {
      const endDate = toDateStr(parseLocalDate(_courseEndDate));
      if (endDate >= todayDateStr()) {
        dates.push({
          id: `od-close-${_courseId}`,
          date: endDate,
          text: `On-demand course ${_coursePrefix} closes`,
          type: 'od_close'
        });
      }
    }

    // Session-based course end (courses without a term field)
    if (!_term && _courseEndDate) {
      dates.push({
        id: _courseId,
        date: _courseEndDate,
        text: `Final day to access ${_coursePrefix}`,
        type: 'session_end'
      });
    }
  });

  dates.sort((a, b) => new Date(a.date) - new Date(b.date));
  return dates;
}

export { buildCourseDates, calcTranscriptDeadline };

export function useCourseDates() {
  const coursesStore = useCoursesStore();
  const userStore = useUserStore();

  const isAccelerate = computed(() => userStore.apis?.learnerData?._partnerURLSubDomain === 'accelerate');

  const enrolledCourses = computed(() =>
    coursesStore.enrollments.filter(
      (course) =>
        course._enrollmentStatus === 'Active' && course._buttonAction.toLowerCase() !== 'access no longer available'
    )
  );

  const allDates = computed(() => buildCourseDates(enrolledCourses.value, isAccelerate.value));

  const upcomingDates = computed(() => {
    const today = todayDateStr();
    return allDates.value.filter((item) => item.date >= today);
  });

  const displayedDates = computed(() => {
    const datesToShow = upcomingDates.value.length ? upcomingDates.value : allDates.value;
    return datesToShow.slice(0, 2);
  });

  const latestUpcomingDate = computed(() => upcomingDates.value[0] || null);

  return {
    enrolledCourses,
    allDates,
    upcomingDates,
    displayedDates,
    latestUpcomingDate,
    formatDateShort
  };
}
